<template>
  <SoAlert
    v-if="queryError"
    title="Failed to render credit check reviews table"
    data-testid="direct-debit-review-error"
  />

  <div>
    <SoDataTableServer
      ref="dataTable"
      v-model:options="options"
      :headers="headers"
      :data="tableData"
      searchable
      search-placeholder="Name or Account Number"
      data-testid="credit-checks-list-table"
      @fetch="fetchData"
    >
      <template #itemActions="rowData">
        <div class="d-flex gap-1 justify-end">
          <SoButton
            title="Mark as Paid"
            type="success"
            data-testid="update-status"
            :disabled="!isEligibleForUpdate(rowData)"
            @click="openUpdateModal(rowData)"
          />
        </div>
      </template>
    </SoDataTableServer>
    <OpsCreditChecksUpdateModal
      v-if="currentRow"
      ref="credit-check-modal"
      v-model="isUpdateModalOpen"
      :current-row="currentRow"
      @submitted="refetchTableData"
    />
  </div>
</template>
<script lang="ts">
  import { defineComponent } from "vue"
  import SoDataTableServer, {
    SoDataTableData,
    SoDataTableOptions,
  } from "@/components/dataTable/SoDataTableServer.vue"
  import { QueryResult } from "@/api/utils/types"
  import {
    RegistrationCreditChecksService,
    RegistrationCreditChecksListResult,
  } from "@/services/operations/RegistrationCreditChecksService"
  import {
    CreditCheckFilters,
    statuses,
    TableFilters,
    tableFilters,
  } from "../../creditChecks/opsCreditChecksReviewConfig"
  import { headers } from "../opsCreditChecksReviewConfig"

  import SoButton from "@/components/formControls/SoButton.vue"
  import dayjs from "dayjs"
  import OpsCreditChecksUpdateModal from "./OpsCreditChecksUpdateModal.vue"
  import { IRegistrationCreditCheck } from "@/models/operations/registrationCreditCheck/registrationCreditCheck.types"

  export default defineComponent({
    components: {
      SoDataTableServer,
      SoButton,
      OpsCreditChecksUpdateModal,
    },
    data() {
      return {
        headers,
        query: null as null | QueryResult<RegistrationCreditChecksListResult>,
        options: {
          filters: tableFilters,
          orderBy: {
            created_at: "desc",
          },
        } as Pick<SoDataTableOptions<TableFilters>, "filters" | "orderBy">,
        currentRow: null as null | IRegistrationCreditCheck,
        isUpdateModalOpen: false,
      }
    },
    computed: {
      tableData(): SoDataTableData {
        const mappedItems = this.query?.data.items?.map((item) => ({
          ...item,

          status: item.status != null ? statuses[item.status] : item.status,
          fullName: `${item.firstName} ${item.lastName}`,
        }))

        return {
          isLoading: !!this.query?.isFetching,
          totalItems: this.query?.data?.total ?? 0,
          items: mappedItems ?? [],
        }
      },
      queryError() {
        return this.query?.isError
      },
    },
    methods: {
      fetchData({ filters, page, pageSize, orderBy, search }) {
        this.query =
          RegistrationCreditChecksService.getRegistrationCreditChecks(
            pageSize,
            (page - 1) * pageSize,
            orderBy,
            search,
            this.mapFilters(filters as TableFilters),
          )
      },
      mapFilters(filters: TableFilters): CreditCheckFilters {
        return {
          status: filters.status.value ?? [],
          depositDeadline: {
            from: filters.depositDeadline.value.from,
            to: filters.depositDeadline.value.to,
          },
        }
      },
      openUpdateModal(rowData) {
        this.currentRow = rowData.item
        this.isUpdateModalOpen = true
      },
      isEligibleForUpdate(rowData) {
        return !!(
          rowData.item.status === "Deposit Requested" &&
          rowData.item.isAccountActive &&
          rowData.item.depositDeadline &&
          !rowData.item.depositDeadline?.isBefore(dayjs())
        )
      },
      async refetchTableData() {
        this.query?.refetch()
      },
    },
  })
</script>
